<template>
  <div class="space-y-8">
    <header class="flex items-start justify-between gap-4">
      <div>
        <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
          <i class="far fa-satellite-dish text-primary-500 mr-2" />
          Convo Radar
        </h2>
        <p class="text-sm text-gray-500 dark:text-gray-400">
          Scheduled AI web searches for local social conversations (Reddit, Facebook groups,
          Nextdoor) where people ask for this client's services.
        </p>
      </div>
      <button class="btn btn-primary btn-sm shrink-0" @click="openForm(null)">
        <i class="far fa-plus mr-1" />
        New Monitor
      </button>
    </header>

    <div
      v-if="!loading && monitors.length === 0"
      class="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 p-8 text-center">
      <i class="far fa-satellite-dish text-3xl text-gray-300 dark:text-gray-600 mb-3" />
      <p class="text-sm font-medium text-gray-700 dark:text-gray-200">
        No monitors yet
      </p>
      <p class="text-sm text-gray-500 mt-1 max-w-md mx-auto">
        Create a monitor for each metro this client serves. It will scan on a schedule and
        collect conversations worth engaging with.
      </p>
    </div>

    <MonitorsList
      v-if="monitors.length > 0"
      :monitors="monitors"
      :running-monitor-ids="runningMonitors"
      @edit="openForm"
      @destroy="destroyMonitor"
      @run-now="runNow" />

    <FindingsFeed
      v-if="monitors.length > 0"
      ref="feed"
      :business-id="businessId"
      :monitors="monitors"
      @changed="loadMonitors" />

    <UiSlidePanel ref="slidePanel" />
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onBeforeUnmount } from 'vue'
import MonitorsList from './monitors_list.vue'
import FindingsFeed from './findings_feed.vue'
import MonitorForm from './monitor_form.vue'
import { UiSlidePanel } from '~/javascript/components/ui/slide_panel'
import { subscribeToSocialMonitors } from '~/javascript/channels/social_monitor_channel'
import { getSocialMonitors, destroySocialMonitor, runMonitorNow } from '~/javascript/api/social_monitors'
import { getBusiness } from '~/javascript/api/businesses'
import { confirmAlert } from '~/javascript/utils/confirm_alert'
import { toastSuccess, toastError } from '~/javascript/utils/toast'

const props = defineProps({
  businessId: { type: String, required: true },
  accountId: { type: String, required: true },
  csrfToken: { type: String, required: true }
})

const monitors = ref([])
const loading = ref(true)
const slidePanel = ref(null)
const feed = ref(null)
const business = ref(null)
// monitor_id -> { completed, total } while a run is in flight
const runningMonitors = reactive({})

let subscription = null
let pollTimer = null

// Fallback for missed ActionCable events: while any monitor shows as scanning,
// poll and clear the scanning state once its latest run has finished.
const reconcileRunning = () => {
  Object.keys(runningMonitors).forEach((id) => {
    const monitor = monitors.value.find((m) => m.id === id)
    const status = monitor?.latest_run?.status
    if (!monitor || status === 'completed' || status === 'failed') {
      delete runningMonitors[id]
      feed.value?.refresh()
    }
  })
}

const loadMonitors = async() => {
  try {
    const { data } = await getSocialMonitors(props.businessId)
    monitors.value = data
  } catch (e) {
    toastError(e)
  } finally {
    loading.value = false
  }
}

const openForm = (monitor) => {
  slidePanel.value.open({
    component: MonitorForm,
    props: {
      businessId: props.businessId,
      monitor,
      defaultCity: business.value?.city || '',
      defaultState: business.value?.state || ''
    },
    events: {
      saved: () => {
        slidePanel.value.close()
        loadMonitors()
      }
    }
  })
}

const destroyMonitor = async(monitor) => {
  const ok = await confirmAlert({
    title: `Delete "${monitor.name}"?`,
    text: 'The monitor and all of its collected findings will be permanently removed.',
    confirmButtonText: 'Yes, delete'
  })
  if (!ok) return

  try {
    await destroySocialMonitor(monitor.id)
    loadMonitors()
    feed.value?.refresh()
  } catch (e) {
    toastError(e)
  }
}

const runNow = async(monitor) => {
  try {
    await runMonitorNow(monitor.id)
    runningMonitors[monitor.id] = { completed: 0, total: monitor.topics.length }
    toastSuccess('Scan started — findings will appear as topics complete.')
  } catch (e) {
    toastError(new Error(e.response?.data?.message || 'Failed to start the scan'))
  }
}

const onMessage = (data) => {
  switch (data.event) {
    case 'run_started':
      runningMonitors[data.monitor_id] = { completed: 0, total: 0 }
      break
    case 'progress':
      runningMonitors[data.monitor_id] = { completed: data.completed, total: data.total }
      break
    case 'run_completed':
      delete runningMonitors[data.monitor_id]
      toastSuccess(`Scan complete — ${data.new_findings} new conversation${data.new_findings === 1 ? '' : 's'} found.`)
      loadMonitors()
      feed.value?.refresh()
      break
    case 'run_failed':
      delete runningMonitors[data.monitor_id]
      toastError(new Error(data.message || 'Monitor run failed'))
      loadMonitors()
      break
    default:
      break
  }
}

onMounted(async() => {
  subscription = subscribeToSocialMonitors(props.businessId, { onMessage })
  pollTimer = setInterval(async() => {
    if (Object.keys(runningMonitors).length === 0) return
    await loadMonitors()
    reconcileRunning()
  }, 10000)
  loadMonitors()
  try {
    const { data } = await getBusiness(props.businessId)
    business.value = data
  } catch {
    business.value = null
  }
})

onBeforeUnmount(() => {
  subscription?.unsubscribe()
  clearInterval(pollTimer)
})
</script>
